import React, { useState } from "react";
import { toast } from "sonner";
import { MagnifyingGlass, Truck, CheckCircle, Package, Clock, PaperPlaneTilt, Plus } from "@phosphor-icons/react";
import Layout from "../components/Layout";
import api from "../lib/api";

const inp = { width: "100%", padding: "9px 11px", border: "1px solid #E2E8F0", borderRadius: 8, fontSize: 13 };
const card = { background: "#fff", border: "1px solid #E2E8F0", borderRadius: 12, padding: 18 };
const th = { textAlign: "left", fontSize: 11, color: "#64748b", fontWeight: 700, padding: "6px 8px", textTransform: "uppercase", letterSpacing: 0.4 };
const td = { padding: "8px 8px", fontSize: 13, borderTop: "1px solid #F1F5F9" };

const Chip = ({ ok, children }) => (
  <span style={{ fontSize: 10, fontWeight: 800, padding: "2px 8px", borderRadius: 999,
    background: ok ? "#DCFCE7" : "#FEE2E2", border: `1px solid ${ok ? "#BBF7D0" : "#FECACA"}`,
    color: ok ? "#166534" : "#991B1B", whiteSpace: "nowrap" }}>{children}</span>
);

const Step = ({ n, title, active, done }) => (
  <div style={{ display: "flex", alignItems: "center", gap: 8, opacity: active || done ? 1 : 0.45 }}>
    <span style={{ width: 24, height: 24, borderRadius: 999, display: "inline-flex", alignItems: "center",
      justifyContent: "center", fontSize: 12, fontWeight: 800,
      background: done ? "#16a34a" : active ? "#0f172a" : "#E2E8F0", color: done || active ? "#fff" : "#64748b" }}>
      {done ? "\u2713" : n}
    </span>
    <span style={{ fontSize: 12, fontWeight: 700, color: active || done ? "#0f172a" : "#64748b" }}>{title}</span>
  </div>
);

const poll = async (jobId, onStep) => {
  for (let i = 0; i < 90; i++) {
    await new Promise((r) => setTimeout(r, 2500));
    const { data } = await api.get(`/sap/dispatch-test/combine/job/${jobId}`, { timeout: 30000 });
    if (onStep) onStep(data);
    if (data.status === "done") return data;
  }
  return { status: "timeout", ok: false };
};

export default function DispatchSO() {
  const [soInput, setSoInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [plan, setPlan] = useState(null);
  const [dispatchId, setDispatchId] = useState("");
  const [stage, setStage] = useState(null);
  const [ship, setShip] = useState(null);
  const [busy, setBusy] = useState("");
  const [liveStep, setLiveStep] = useState("");

  const loadSo = async () => {
    const q = soInput.trim();
    if (!q) { toast.error("Enter a Sales Order number"); return; }
    setLoading(true); setPlan(null); setStage(null); setShip(null);
    try {
      const { data } = await api.get("/sap/dispatch-test/load", { params: { so: q }, timeout: 90000 });
      if (!data?.ok) { toast.error(data?.reason || "Sales Order not found"); setLoading(false); return; }
      const lines = (data.lines || []).map((l) => ({ ...l, include: true, ship_qty: l.open_qty }));
      if (!lines.length) toast.message("Loaded — but this order has no open lines to ship.");
      setPlan({ so_id: data.so_id || q, lines });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Load failed");
    }
    setLoading(false);
  };

  const setLine = (i, patch) => setPlan((p) => {
    const lines = p.lines.slice(); lines[i] = { ...lines[i], ...patch }; return { ...p, lines };
  });

  const selected = (plan?.lines || []).filter((l) => l.include && Number(l.ship_qty) > 0);
  const overQty = selected.find((l) => Number(l.ship_qty) > Number(l.open_qty));
  const qtyByLine = () => selected.map((l) => ({
    so_id: plan.so_id, product: l.product, open_qty: l.open_qty, ship_qty: Number(l.ship_qty),
  }));

  const createOdr = async () => {
    if (!selected.length) { toast.error("Select at least one item with a ship quantity"); return; }
    if (overQty) { toast.error(`${overQty.product}: ship qty exceeds open ${overQty.open_qty}`); return; }
    if (!dispatchId.trim()) { toast.error("Enter a Dispatch Plan ID"); return; }
    setBusy("stage"); setStage(null); setShip(null); setLiveStep("attaching lines…");
    try {
      const { data } = await api.post("/sap/dispatch-test/stage", {
        so_id: plan.so_id, qty_by_line: qtyByLine(), dispatch_number: dispatchId.trim(),
      }, { timeout: 30000 });
      const res = await poll(data.job_id, (d) => setLiveStep((d.steps || []).slice(-1)[0]?.step || "working…"));
      setStage(res);
      if (res.ok) toast.success("ODR created in SAP — pick task staged");
      else toast.error(res.error || "Create ODR did not complete");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Create ODR failed");
    }
    setBusy(""); setLiveStep("");
  };

  const releaseOd = async () => {
    if (!dispatchId.trim()) { toast.error("Enter a Dispatch Plan ID"); return; }
    setBusy("ship"); setShip(null); setLiveStep("confirming goods issue…");
    try {
      const { data } = await api.post("/sap/dispatch-test/ship", {
        so_id: plan.so_id, qty_by_line: qtyByLine(), dispatch_number: dispatchId.trim(),
      }, { timeout: 30000 });
      const res = await poll(data.job_id, (d) => setLiveStep((d.steps || []).slice(-1)[0]?.step || "working…"));
      setShip(res);
      const dids = res.deliveries || (res.delivery_id ? [res.delivery_id] : []);
      if (res.ok && dids.length) toast.success(`Outbound delivery released: ${dids.join(", ")}`);
      else if (res.delivery_pending) toast.message("Goods issue posted — delivery number materialising in SAP");
      else toast.error(res.error || "Release did not complete");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Release failed");
    }
    setBusy(""); setLiveStep("");
  };

  const shipDids = ship ? (ship.deliveries || (ship.delivery_id ? [ship.delivery_id] : [])) : [];
  const shipStampedAll = ship && (ship.stamps || []).length && (ship.stamps || []).every((s) => s.ok);

  return (
    <Layout breadcrumb={["Setup", "Dispatch"]}>
      <div style={{ display: "flex", flexDirection: "column", gap: 16, maxWidth: 1000 }}>
        <div>
          <h1 style={{ display: "flex", alignItems: "center", gap: 10, fontSize: 22, fontWeight: 800, margin: 0 }}>
            <Truck size={24} weight="duotone" /> Dispatch a Sales Order
          </h1>
          <p style={{ color: "#64748b", fontSize: 13, margin: "6px 0 0" }}>
            One Sales Order &rarr; one outbound delivery (full or partial), stamped with your Dispatch Plan ID.
            Create the ODR in SAP, review it, then release the outbound delivery.
          </p>
        </div>

        <div style={{ ...card, display: "flex", gap: 22, alignItems: "center", flexWrap: "wrap", padding: "12px 18px" }}>
          <Step n={1} title="Pick SO & items" active={!!plan && !stage} done={!!stage} />
          <Step n={2} title="Dispatch Plan ID" active={!!plan && !stage} done={!!stage} />
          <Step n={3} title="Create ODR" active={!!plan && !stage} done={!!(stage && stage.ok)} />
          <Step n={4} title="Review" active={!!(stage && stage.ok) && !ship} done={!!ship} />
          <Step n={5} title="Release OD" active={!!(stage && stage.ok)} done={!!(ship && ship.ok && shipDids.length)} />
        </div>

        <div style={card}>
          <div style={{ fontWeight: 800, fontSize: 14, marginBottom: 10 }}>1 &middot; Pick a Sales Order</div>
          <div style={{ display: "flex", gap: 8 }}>
            <input data-testid="dp-so-input" value={soInput} onChange={(e) => setSoInput(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && loadSo()} placeholder="Sales Order # (e.g. 12807)"
              style={{ ...inp, maxWidth: 260 }} />
            <button data-testid="dp-load-btn" onClick={loadSo} disabled={loading}
              style={{ display: "inline-flex", alignItems: "center", gap: 6, fontSize: 13, fontWeight: 700,
                padding: "9px 16px", borderRadius: 8, border: "none", background: "#0f172a", color: "#fff",
                cursor: loading ? "default" : "pointer", opacity: loading ? 0.6 : 1 }}>
              <MagnifyingGlass size={15} weight="bold" /> {loading ? "Loading…" : "Load"}
            </button>
          </div>
        </div>

        {plan && (
          <div style={card} data-testid="dp-plan">
            <div style={{ fontWeight: 800, fontSize: 14, marginBottom: 4 }}>
              2 &middot; Add items to the dispatch plan &mdash; SO {plan.so_id}
            </div>
            <div style={{ fontSize: 12, color: "#64748b", marginBottom: 12 }}>
              Tick the lines to ship and set the quantity (default = full open qty; lower it for a partial).
            </div>
            <table style={{ width: "100%", borderCollapse: "collapse" }}>
              <thead><tr>
                <th style={{ ...th, width: 36 }}></th>
                <th style={th}>Product</th><th style={th}>Site</th>
                <th style={th}>Open</th><th style={th}>Stock</th><th style={th}>Ship qty</th>
              </tr></thead>
              <tbody>
                {plan.lines.map((l, i) => (
                  <tr key={i} data-testid={`dp-line-${l.product}`} style={{ opacity: l.include ? 1 : 0.5 }}>
                    <td style={td}>
                      <input type="checkbox" data-testid={`dp-incl-${l.product}`} checked={!!l.include}
                        onChange={(e) => setLine(i, { include: e.target.checked })} />
                    </td>
                    <td style={td}><b>{l.product}</b>{l.description ? <div style={{ fontSize: 11, color: "#94a3b8" }}>{l.description}</div> : null}</td>
                    <td style={td}>{l.site || "—"}</td>
                    <td style={td}>{l.open_qty} {l.unit || "EA"}</td>
                    <td style={td}>{l.available != null ? <Chip ok={l.available >= Number(l.ship_qty || 0)}>{l.available} avail</Chip> : "—"}</td>
                    <td style={td}>
                      <input data-testid={`dp-qty-${l.product}`} type="number" min="0" max={l.open_qty}
                        value={l.ship_qty} disabled={!l.include}
                        onChange={(e) => setLine(i, { ship_qty: e.target.value })}
                        style={{ ...inp, width: 90, padding: "6px 8px" }} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>

            <div style={{ display: "flex", gap: 12, alignItems: "flex-end", marginTop: 16, flexWrap: "wrap" }}>
              <label style={{ fontSize: 12, fontWeight: 700, color: "#334155" }}>
                Dispatch Plan ID
                <input data-testid="dp-dispatch-id" value={dispatchId} onChange={(e) => setDispatchId(e.target.value)}
                  placeholder="e.g. DISP-12807" style={{ ...inp, width: 220, marginTop: 4,
                    borderColor: dispatchId.trim() ? "#E2E8F0" : "#f87171" }} />
              </label>
              <button data-testid="dp-create-odr-btn" onClick={createOdr}
                disabled={busy === "stage" || !selected.length || !dispatchId.trim()}
                style={{ display: "inline-flex", alignItems: "center", gap: 6, fontSize: 13, fontWeight: 700,
                  padding: "10px 18px", borderRadius: 8, border: "none",
                  background: (busy === "stage" || !selected.length || !dispatchId.trim()) ? "#c7d2fe" : "#4f46e5",
                  color: "#fff", cursor: (busy === "stage" || !selected.length || !dispatchId.trim()) ? "default" : "pointer" }}>
                <Plus size={15} weight="bold" /> {busy === "stage" ? "Creating ODR…" : "Create ODR in SAP"}
              </button>
              {busy === "stage" && <span style={{ fontSize: 12, color: "#64748b" }}>{liveStep}</span>}
            </div>
          </div>
        )}

        {stage && (
          <div style={card} data-testid="dp-stage-result">
            <div style={{ display: "flex", alignItems: "center", gap: 8, fontWeight: 800, fontSize: 14, marginBottom: 10 }}>
              {stage.ok ? <CheckCircle size={18} weight="fill" color="#16a34a" /> : <Package size={18} weight="fill" color="#dc2626" />}
              3 &middot; What got created in SAP
              <span style={{ marginLeft: "auto", fontSize: 12, color: "#64748b", display: "inline-flex", alignItems: "center", gap: 4 }}>
                <Clock size={13} /> {stage.elapsed_seconds}s
              </span>
            </div>
            {stage.ok ? (
              <div style={{ fontSize: 13, color: "#334155", lineHeight: 1.7 }}>
                <div><Chip ok>ODR staged</Chip> &nbsp; Pick task <b>{stage.task_uuid || (stage.task_pending ? "materialising…" : "—")}</b></div>
                <div>Ship-from site: <b>{stage.site || "—"}</b></div>
                <div style={{ marginTop: 6 }}>Items in this dispatch:&nbsp;
                  {selected.map((l) => `${l.product}×${l.ship_qty}`).join(", ")}</div>
                {stage.note && <div style={{ fontSize: 12, color: "#64748b", marginTop: 6 }}>{stage.note}</div>}
              </div>
            ) : (
              <div style={{ fontSize: 13, color: "#991B1B" }}>{stage.error || "Create ODR did not complete."}</div>
            )}

            {stage.ok && (
              <div style={{ marginTop: 16, paddingTop: 16, borderTop: "1px solid #F1F5F9" }}>
                <div style={{ fontWeight: 800, fontSize: 14, marginBottom: 8 }}>4 &middot; Release the outbound delivery</div>
                <button data-testid="dp-release-btn" onClick={releaseOd}
                  disabled={busy === "ship" || !!(ship && ship.ok && shipDids.length)}
                  style={{ display: "inline-flex", alignItems: "center", gap: 6, fontSize: 13, fontWeight: 700,
                    padding: "10px 18px", borderRadius: 8, border: "none",
                    background: (busy === "ship" || !!(ship && ship.ok && shipDids.length)) ? "#99f6e4" : "#0f766e",
                    color: "#fff", cursor: (busy === "ship" || !!(ship && ship.ok && shipDids.length)) ? "default" : "pointer" }}>
                  <PaperPlaneTilt size={15} weight="bold" />
                  {busy === "ship" ? "Releasing…" : (ship && shipDids.length ? "Released ✓" : "Release Outbound Delivery")}
                </button>
                {busy === "ship" && <span style={{ fontSize: 12, color: "#64748b", marginLeft: 10 }}>{liveStep}</span>}
              </div>
            )}
          </div>
        )}

        {ship && (
          <div style={card} data-testid="dp-ship-result">
            <div style={{ display: "flex", alignItems: "center", gap: 8, fontWeight: 800, fontSize: 14, marginBottom: 10 }}>
              {shipDids.length ? <CheckCircle size={18} weight="fill" color="#16a34a" /> : <Package size={18} weight="fill" color="#d97706" />}
              5 &middot; SAP confirmation
              <span style={{ marginLeft: "auto", fontSize: 12, color: "#64748b", display: "inline-flex", alignItems: "center", gap: 4 }}>
                <Clock size={13} /> {ship.elapsed_seconds}s
              </span>
            </div>
            {shipDids.length ? (
              <div style={{ fontSize: 13, color: "#334155", lineHeight: 1.8 }}>
                <div>Outbound delivery: <b data-testid="dp-delivery-id">{shipDids.join(", ")}</b> &nbsp;<Chip ok>Goods issue posted</Chip></div>
                <div data-testid="dp-stamp-line">
                  Dispatch Plan ID <b>{dispatchId}</b> &nbsp;
                  {shipStampedAll ? <Chip ok>stamped ✓</Chip> : <Chip ok={false}>stamp {(ship.stamps || []).filter((s) => s.ok).length}/{(ship.stamps || []).length || "?"}</Chip>}
                </div>
                {(ship.ship_summary?.lines || []).length > 0 && (
                  <table style={{ width: "100%", borderCollapse: "collapse", marginTop: 8, maxWidth: 420 }}>
                    <thead><tr><th style={th}>Product</th><th style={th}>Shipped</th><th style={th}>Still open</th></tr></thead>
                    <tbody>{ship.ship_summary.lines.map((l, i) => (
                      <tr key={i}><td style={td}>{l.product}</td><td style={td}>{l.shipped}</td><td style={td}>{l.open}</td></tr>
                    ))}</tbody>
                  </table>
                )}
              </div>
            ) : (
              <div style={{ fontSize: 13, color: "#92400e" }}>
                {ship.delivery_pending
                  ? "Goods issue posted — the delivery number is materialising in SAP and will appear shortly."
                  : (ship.error || "Release did not complete.")}
              </div>
            )}
          </div>
        )}
      </div>
    </Layout>
  );
}
